import { createElement, forwardRef, type ReactNode } from "react";

import { cn } from "../utils/cn";
import { resolveIconColor } from "./colors";
import {
  type CreateIconOptions,
  ROOT_FILL_BY_PAINT,
  STROKE_GRID_PIXELS,
} from "./createIconOptions";
import { type IconNode } from "./iconNode";
import { type IconComponent, type IconProps } from "./iconProps";
import { isIconSizeToken } from "./isIconSizeToken";
import { resolveIconSquareDimension } from "./resolveIconSquareDimension";
import { DEFAULT_ICON_SIZE, DEFAULT_STROKE_WIDTH, SIZE_CLASS } from "./sizes";

// `tag` is a runtime string, not a JSX-known element name, so this goes
// through createElement — JSX types it IntrinsicAttributes, no children slot.
function renderIconNode(node: IconNode): ReactNode[] {
  return node.map(([tag, attrs, children], index) =>
    createElement(
      tag,
      { key: index, ...attrs },
      children && renderIconNode(children),
    ),
  );
}

// Shared factory that collapses the ~70 lines of per-icon forwardRef
// boilerplate down to a single line at the call site:
//
//   export const FooIcon = createIcon("FooIcon", [["path", { d: "..." }]]);
//
// `displayName` is required as the second arg because `forwardRef` strips
// the function name from the resulting component. React DevTools falls
// back to `Function.name`, which is `anonymous` here — passing it
// explicitly keeps DevTools labels aligned with the per-icon file's
// export binding. The generator writes both from the same source so they
// cannot drift.
//
// The `options` argument also lets hand-authored Air icons through: their
// own `viewBox`, and a `paint` mode. `paint` only decides what the root
// element paints with `currentColor`, so `fixed` — art whose colors are its
// own — is the mode where the root paints nothing at all.
export function createIcon(
  displayName: string,
  node: IconNode,
  options: CreateIconOptions = {},
): IconComponent {
  const { viewBox = "0 0 24 24", paint = "stroke" } = options;
  const isStroke = paint === "stroke";
  const Icon = forwardRef<SVGSVGElement, IconProps>(
    (
      {
        size = DEFAULT_ICON_SIZE,
        color: colorProp,
        unstyled,
        className,
        style,
        ...restOfProps
      },
      ref,
    ) => {
      // No inline `color` when the prop is omitted: the SVG's own
      // `stroke="currentColor"` then inherits from the nearest ancestor
      // that sets a CSS `color`, same as the hand-copied icons before ADS.
      const resolvedColor =
        colorProp !== undefined ? resolveIconColor(colorProp) : undefined;
      const hasA11yProp = Object.keys(restOfProps).some(
        (key) =>
          key === "aria-hidden" ||
          key === "title" ||
          key === "role" ||
          key.startsWith("aria-"),
      );
      const usesTokenSizeClass = !unstyled && isIconSizeToken(size);
      const resolvedSquareDimension = resolveIconSquareDimension(
        size,
        isStroke ? STROKE_GRID_PIXELS : undefined,
      );
      // `unstyled` drops the design-system sizing class and inline color —
      // the consumer owns styling via `className` or `style`. Numeric and
      // percentage `size` values still set square width/height on the SVG root.
      // Stroke stays DEFAULT_STROKE_WIDTH viewBox units so the renderer
      // scales it with the box (2 * box/24). Multiplying by size here
      // would scale twice, and CSS/percentage boxes would not.
      return (
        <svg
          aria-hidden={hasA11yProp ? undefined : "true"}
          className={cn(
            usesTokenSizeClass ? SIZE_CLASS[size] : undefined,
            className,
          )}
          fill={ROOT_FILL_BY_PAINT[paint]}
          height={resolvedSquareDimension}
          ref={ref}
          stroke={isStroke ? "currentColor" : undefined}
          strokeLinecap={isStroke ? "round" : undefined}
          strokeLinejoin={isStroke ? "round" : undefined}
          strokeWidth={isStroke ? DEFAULT_STROKE_WIDTH : undefined}
          style={{
            ...(!unstyled && resolvedColor !== undefined
              ? { color: resolvedColor }
              : {}),
            ...style,
          }}
          viewBox={viewBox}
          width={resolvedSquareDimension}
          xmlns="http://www.w3.org/2000/svg"
          {...restOfProps}
        >
          {renderIconNode(node)}
        </svg>
      );
    },
  );
  Icon.displayName = displayName;
  return Icon;
}
